import React from "react";
import { ThreeDSettingsProps } from "./interfaces";
import { Highlight } from "./maybe_highlight";
import { Actions, DeviceSetting, ToolTips } from "../constants";
import { Header } from "./hardware_settings/header";
import { Collapse } from "@blueprintjs/core";
import {
  BlurableInput, DropDownItem, FBSelect, Help, Row, ToggleButton,
} from "../ui";
import { t } from "../i18next_wrapper";
import { TaggedFarmwareEnv } from "farmbot";
import { isUndefined } from "lodash";
import { edit, initSave, save } from "../api/crud";
import { getModifiedClassNameSpecifyDefault } from "./default_values";
import { Config, SurfaceDebugOption } from "../three_d_garden/config";

const DEFAULTS: Partial<Record<keyof Config, number>> = {
  bedWallThickness: 40,
  bedHeight: 300,
  ccSupportSize: 50,
  beamLength: 1500,
  columnLength: 500,
  zAxisLength: 1000,
  bedXOffset: 140,
  bedYOffset: 20,
  bedZOffset: 0,
  legSize: 100,
  legsFlush: 1,
  extraLegsX: 1,
  extraLegsY: 0,
  bedBrightness: 8,
  soilBrightness: 12,
  clouds: 1,
  laser: 0,
  stats: 0,
  threeAxes: 0,
  solar: 0,
  lowDetail: 0,
  eventDebug: 0,
  cableDebug: 0,
  lightsDebug: 0,
  moistureDebug: 0,
  surfaceDebug: SurfaceDebugOption.none,
  ambient: 75,
  sun: 75,
  heading: 0,
  sunAzimuth: 230,
  sunInclination: 140,
  bounds: 0,
  grid: 1,
  scene: 0,
  tracks: 1,
  cableCarriers: 1,
  axes: 0,
  xyDimensions: 0,
  zDimension: 0,
  people: 0,
  desk: 0,
};

// 场景选项：用于 3D 渲染的英文值（保持不变，用于 config.scene 映射）
export const SCENES: Record<number, string> = {
  0: "Outdoor",
  1: "Lab",
  2: "Greenhouse",
};

// 场景翻译键（用于国际化）
const SCENE_KEYS: Record<number, string> = {
  0: "3D Garden Outdoor",
  1: "3D Garden Lab",
  2: "3D Garden Greenhouse",
};

export const namespace3D = (key: string): string => "3D_" + key;

const find =
  (envs: TaggedFarmwareEnv[], key: string): TaggedFarmwareEnv | undefined =>
    envs.filter(env => env.body.key == namespace3D(key))[0];

export const get3DConfigValueFunction = (envs: TaggedFarmwareEnv[]) =>
  (key: keyof Config): number => {
    const maybe = find(envs, key);
    const raw = isUndefined(maybe) ? DEFAULTS[key] : maybe.body.value;

    // scene 特殊处理：支持字符串或数字，最终返回数字
    if (key === "scene") {
      const rawStr = "" + raw;
      const num = parseFloat(rawStr);
      // 如果是有效的数字，直接返回
      if (!isNaN(num) && num >= 0 && num <= 2) {
        return num;
      }
      // 如果是字符串，尝试反向查找
      const strNum = Object.entries(SCENES).find(([k, v]) => v === rawStr)?.[0];
      return parseFloat(strNum) || 0;
    }

    // 其他配置返回数字
    return parseFloat("" + raw);
  };

export const findOrCreate3DConfigFunction =
  (dispatch: Function, envs: TaggedFarmwareEnv[]) =>
    (key: keyof Config, value: string) => {
      const maybe = find(envs, key);
      if (isUndefined(maybe)) {
        if (value != "" + DEFAULTS[key]) {
          dispatch(initSave("FarmwareEnv", { key: namespace3D(key), value }));
        }
      } else {
        dispatch(edit(maybe, { value }));
        dispatch(save(maybe.uuid));
      }
    };

interface ThreeDConfigProps {
  dispatch: Function;
  distanceIndicator?: string;
  setting: DeviceSetting;
  configKey: keyof Config;
  tooltip: string;
  getValue(key: string): number;
  findOrCreate(key: string, value: string): void;
  isToggle?: boolean;
  isScene?: boolean;
}

export const ThreeDConfig = (props: ThreeDConfigProps) => {
  const { dispatch, configKey, distanceIndicator } = props;
  const value = props.getValue(configKey);
  const modifiedClassName = getModifiedClassNameSpecifyDefault(
    value, DEFAULTS[configKey]);
  const action = (newValue: string) => props.findOrCreate(configKey, newValue);

  // 动态生成场景选项（支持国际化）
  const sceneOptions: DropDownItem[] = Object.entries(SCENE_KEYS).map(([key, keyString]) => ({
    label: t(keyString),
    value: Number(key),
  }));

  return <Highlight settingName={props.setting}>
    <Row className="grid-2-col">
      <div className={"labels"}>
        <label>
          {t(props.setting)}
        </label>
        <Help
          text={t(props.tooltip, {
            defaultConfigValue: "" + DEFAULTS[configKey],
          })}
          setOpen={() => dispatch({
            type: Actions.SET_DISTANCE_INDICATOR,
            payload: distanceIndicator ? "" : configKey,
          })} />
      </div>
      {props.isToggle &&
        <ToggleButton
          className={modifiedClassName}
          toggleValue={value}
          toggleAction={() => action(value ? "0" : "1")} />}
      {props.isScene &&
        <FBSelect
          list={sceneOptions}
          selectedItem={sceneOptions.find(o => o.value === value)}
          onChange={ddi => action("" + ddi.value)} />}
      {!props.isToggle && !props.isScene &&
        <BlurableInput
          type="number"
          wrapperClassName={modifiedClassName}
          value={value}
          onCommit={e => action(e.currentTarget.value)} />}
    </Row>
  </Highlight>;
};

export const ThreeDSettings = (props: ThreeDSettingsProps) => {
  const { dispatch, distanceIndicator } = props;
  const getValue = get3DConfigValueFunction(props.farmwareEnvs);
  const findOrCreate = findOrCreate3DConfigFunction(dispatch, props.farmwareEnvs);
  const common = { dispatch, getValue, findOrCreate, distanceIndicator };
  return <Highlight className={"section"}
    settingName={DeviceSetting.threeDGarden}>
    <Header
      title={DeviceSetting.threeDGarden}
      panel={"three_d"}
      dispatch={props.dispatch}
      expanded={props.settingsPanelState.three_d} />
    <Collapse isOpen={!!props.settingsPanelState.three_d}>
      <ThreeDConfig {...common}
        tooltip={ToolTips.THREE_D_BED_WALL_THICKNESS}
        setting={DeviceSetting.bedWallThickness}
        configKey={"bedWallThickness"} />
      <ThreeDConfig {...common}
        tooltip={ToolTips.THREE_D_BED_HEIGHT}
        setting={DeviceSetting.bedHeight}
        configKey={"bedHeight"} />
      <ThreeDConfig {...common}
        tooltip={ToolTips.THREE_D_CC_SUPPORT_SIZE}
        setting={DeviceSetting.ccSupportSize}
        configKey={"ccSupportSize"} />
      <ThreeDConfig {...common}
        tooltip={ToolTips.THREE_D_BEAM_LENGTH}
        setting={DeviceSetting.beamLength}
        configKey={"beamLength"} />
      <ThreeDConfig {...common}
        tooltip={ToolTips.THREE_D_COLUMN_LENGTH}
        setting={DeviceSetting.columnLength}
        configKey={"columnLength"} />
      <ThreeDConfig {...common}
        tooltip={ToolTips.THREE_D_Z_AXIS_LENGTH}
        setting={DeviceSetting.zAxisLength}
        configKey={"zAxisLength"} />
      <ThreeDConfig {...common}
        tooltip={ToolTips.THREE_D_BED_X_OFFSET}
        setting={DeviceSetting.bedXOffset}
        configKey={"bedXOffset"} />
      <ThreeDConfig {...common}
        tooltip={ToolTips.THREE_D_BED_Y_OFFSET}
        setting={DeviceSetting.bedYOffset}
        configKey={"bedYOffset"} />
      <ThreeDConfig {...common}
        tooltip={ToolTips.THREE_D_BED_Z_OFFSET}
        setting={DeviceSetting.bedZOffset}
        configKey={"bedZOffset"} />
      <ThreeDConfig {...common}
        tooltip={ToolTips.THREE_D_LEG_SIZE}
        setting={DeviceSetting.legSize}
        configKey={"legSize"} />
      <ThreeDConfig {...common}
        tooltip={ToolTips.THREE_D_BOUNDS}
        setting={DeviceSetting.bounds}
        configKey={"bounds"}
        isToggle={true} />
      <ThreeDConfig {...common}
        tooltip={ToolTips.THREE_D_GRID}
        setting={DeviceSetting.grid}
        configKey={"grid"}
        isToggle={true} />
      <ThreeDConfig {...common}
        tooltip={ToolTips.THREE_D_ENVIRONMENT}
        setting={DeviceSetting.environment}
        configKey={"scene"}
        isScene={true} />
    </Collapse>
  </Highlight>;
};
